import { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import {
  Loader,
  Dialog,
  IconInfo,
  SimpleTable,
  SimpleTableRow,
  SimpleTableData,
  SimpleTableBody
} from 'cdk-radial';
import { connect } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import {
  getApiIntegrationsDetails
} from '../../../../redux/connectedComponent/apiIntegrations/apiIntegration.slice';
import { setScreen } from '../../../../redux/connectedComponent/uiFactory/uiFactory.slice'
import { screens } from '../../../config/uiFactory.config'
import LearnMore from '../../../LearnMore/LearnMore';
import  './index.scss';
import { sendAmplitudeData } from '../../../../utils/amplitude'
import { AMPLITUDE_EVENTS, AMPLITUDE_PROPERTIES } from '../../../../constants/amplitudeConstants';
import { trackPageView } from '../../../../utils/gtm';
import ApiIntegrationsTable from './ApiIntegrationsTable';
import { environmentURLs } from '../../../common/CaptureEnvironment';
import { getAPISystemInfo } from '../../../../redux/connectedComponent/configurations/Configurations.slice';

const StripeTableRow = styled(SimpleTableRow)`
    :nth-child(even) {
        background-color: ${({ theme }) => theme.color.zebraStripe.background.value};
        :hover {
            background-color: ${({ theme }) => theme.color.zebraStripe.hover.value};
        }
    }
`;

const ApiIntegrationsPage = ({
  solutionData = {overview: {}},
  subscriptionData = {},
  selectedPlanId = '',
  isBulkSubscribed
}) => {
  const dispatch = useDispatch();
  const state = useSelector(state => state);
  const token =
    state.loginDetailsSlice && state.loginDetailsSlice.authenticationToken;
  let solutionId = solutionData.id;
  const { apiProviders = [], locationsGroupByApi = {}, solution = {} } =
    state.apiIntegrationSlice &&
    state.apiIntegrationSlice.ApiIntegrationsDetails;

  const [showFields, setShowFields] = useState(false);
  const [showFieldsProviderIndex, setShowFieldsProviderIndex] = useState(0);
  const {systemInfoPending} = state.ConfigurationsSlice;

  const sortedProviders = useMemo(() => {
    return apiProviders && [...apiProviders].sort((a, b) => {
      let x = a && a.apiDisplayName ? a.apiDisplayName : undefined;
      let y = b && b.apiDisplayName ? b.apiDisplayName : undefined;

      if (x && y) {
        if (x < y) return -1;
        if (x > y) return 1;
      }
      return 0;
    });
  }, [apiProviders]);

  const fieldsShownProvider = useMemo(() => {
    return sortedProviders?.[showFieldsProviderIndex] ?? {};
  }, [sortedProviders, showFieldsProviderIndex]);

  const COLOR_STROKE = '#006CFA';
  const LOADER_STYLE = {
    stroke: COLOR_STROKE,
    margin: '150px 70px',
    display: 'flex'
  };


  async function getApiIntegrationsDetailsWrapper(payload) {
    dispatch(getApiIntegrationsDetails({payload,token}));
  }

  const onShowFields = useCallback(index => {
    setShowFieldsProviderIndex(index);
    setShowFields(true);
  }, []);

  const onManageClickHandler = ({apiProvider, isNewApi}) => {
    sendAmplitudeData(AMPLITUDE_EVENTS.MANAGE_API,{
      [AMPLITUDE_PROPERTIES.APP_NAME]: solutionData?.overview?.name || solutionData?.displayName,
      [AMPLITUDE_PROPERTIES.APP_ID]: solutionData?.id,
      [AMPLITUDE_PROPERTIES.API_NAME]:  apiProvider?.apiDisplayName || apiProvider?.name,
      [AMPLITUDE_PROPERTIES.API_ID]: apiProvider?.id,
    })
    dispatch(setScreen({
      screen: environmentURLs.ENABLE_MANAGE_INTEGRATIONS_V2 ? screens.MANAGE_INTEGRATIONS_V2 : screens.Manage_BulkConnections,
      solutionData: solutionData,
      additionalData: {
        subscriptionData,
        selectedPlanId,
        apiDetails: apiProvider,
        isNewApi,
        isBulkSubscribed
      }
    }));
  };


  useEffect(()=>{
    trackPageView('Manage Integration');
    const entities = subscriptionData.map(dealer => dealer.entityId);
    getApiIntegrationsDetailsWrapper({entities,solutionId})
  },[])

  useEffect(() => {
    if(apiProviders.length) {
      const apiList = apiProviders.map(({id, apiType}) => ({id, apiType}));
      if(apiList.length) {
        dispatch(getAPISystemInfo({apis: apiList, appId: solutionId, token}));
      };
    };
  }, [apiProviders]);

  return (
    <>
      <div className="api-integration">
        {solution && (
          <>
            <div className="api-integration__header">
              <FormattedMessage
                id="Solution.subscribedOn"
                defaultMessage={'API Integrations'}
              />
            </div>
            <div className="api-integration__description">
              <FormattedMessage
                id="Solution.Description"
                defaultMessage={
                  'View the API details and manage the APIs integrated into this app.'
                }
              />
              {' '}
              <LearnMore
                docPath="/docs/marketplace/marketplace-for-apps/managing-app-configurations/#managing-api-integrations"
                screen="Manage Integrations"
              />
            </div>
            {
             ((isBulkSubscribed && solutionData?.isAppNotListed) )&&
              <div className='api-integration__manage-not-allowed'>
                <IconInfo />
                <div className='api-integration__not-allowed-message'>Fortellis Admin enabled apps that are not listed in Marketplace are not allowed to manage API Integrations.<a className="api-integration__mailto" href={`mailto:support@fortellis.io`}>Contact Fortellis Support.</a></div>
              </div>
            }
          </>
        )}
        <div className="api-integration__container">
          {state.apiIntegrationSlice.isApiIntegrationsDetailsPending || systemInfoPending ? (
            <Loader
              hideLabel
              label="Loading API integrations..."
              style={LOADER_STYLE}
            />
          ) : (
            <ApiIntegrationsTable
              apiProviders={sortedProviders}
              locationsGroupByApi={locationsGroupByApi}
              solutionData={solutionData}
              subscriptionData={subscriptionData}
              isBulkSubscribed={isBulkSubscribed}
              onShowFields={onShowFields}
              solution ={solution}
              onManageConnections={onManageClickHandler}
            />
          )}
        </div>
      </div>
      <Dialog
        title={`Fields for ${fieldsShownProvider.apiDisplayName}`}
        isOpen={showFields}
        id="api-integration__fields-dialog"
        onClose={() => setShowFields(false)}
      >
        <SimpleTable>
          <SimpleTableBody>
            {(fieldsShownProvider.fields || []).map(field => (
              <StripeTableRow key={field}>
                <SimpleTableData>{field}</SimpleTableData>
              </StripeTableRow>
            ))}
          </SimpleTableBody>
        </SimpleTable>
      </Dialog>
    </>
  );
};

const mapStateToProps = state => state;

export default connect(mapStateToProps)(ApiIntegrationsPage);
